import React, { useEffect, useState } from "react";
import { Layout, theme, Form, Modal, Input, Button } from "antd";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Navbar, Footer as Footy } from "../../components";
import "./Home.css";
import {
  getFavourites,
  getOrder,
  getProfile,
  resetPasswordUser,
} from "../../services/userDataService";
import { getCart } from "../../services/cartDataService";
import { useDispatch, useSelector } from "react-redux";
import {
  setUser,
  setFavourites,
  setRole,
} from "../../state/reducers/userSlice";
import { setCart } from "../../state/reducers/cartSlice";
import {
  getAllProducts,
  getBestSellers,
  // getFeaturedProducts,
  getNewProducts,
  getTodaysDealProducts,
} from "../../services/productDataService";
import {
  setBestSellers,
  setNewProducts,
  setProducts,
  setTodaysDealProducts,
} from "../../state/reducers/productSlice";
import { setOrder } from "../../state/reducers/orderSlice";
import { setShopProducts } from "../../state/reducers/shopSlice";
import ErrorBoundary from "../../components/error/ErrorBoundary";
import { message } from "antd";
import GlobalErrorDisplay from "../../components/error/GlobalErrorDisplay";
import { jwtDecode } from "jwt-decode";

const { Header, Footer, Content } = Layout;

const Home = () => {
  const token = useSelector((state) => state.user.token);
  const location = useLocation();
  const [newPasswordVisible, setNewPasswordVisible] = useState(false); // Modal state for setting a new password
  const navigate = useNavigate();
  const [newPasswordForm] = Form.useForm(); // Separate form for new password
  const maintoken = useSelector((state) => state.user.token);
  const errorFallback = (error, errorInfo) => {
    // Display a message using Ant Design's message component
    message.error("An error occurred: " + error.toString());

    // Render a fallback UI with the error message
    return (
      <div className="error-fallback">
        <h2>Something went wrong.</h2>
        <details style={{ whiteSpace: "pre-wrap" }}>
          {error && error.toString()}
          <br />
          {errorInfo.componentStack}
        </details>
      </div>
    );
  };

  const handleNewPasswordSubmit = async (values) => {
    try {
      // Call your API to set the new password here
      // Assuming you have an API service for updating the password
      const response = await resetPasswordUser(token, values.newPassword);
      console.log(response);
      message.success("Password updated successfully");
      setNewPasswordVisible(false); // Close the modal on success
      newPasswordForm.resetFields();
      navigate("/"); // Redirect the user after successfully setting a new password
    } catch (err) {
      message.error("Failed to update password");
    }
  };

  const dispatch = useDispatch();

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const getUser = async () => {
    try {
      const user = await getProfile(maintoken);
      dispatch(setUser(user.data.data));
      const decoded = jwtDecode(maintoken);
      dispatch(setRole(decoded.role));
      const favourites = await getFavourites(maintoken);
      dispatch(setFavourites(favourites.data.data.products || null));
    } catch (err) {
      return;
    }
  };

  const getCartItems = async () => {
    try {
      const cart = await getCart(maintoken);
      dispatch(setCart(cart.data.data.items));
    } catch (err) {
      return;
    }
  };

  const getOrderItems = async () => {
    try {
      const order = await getOrder(maintoken);
      dispatch(setOrder(order.data.data));
    } catch (err) {
      return;
    }
  };

  const getProducts = async () => {
    try {
      const products = await getAllProducts();
      const newProducts = await getNewProducts();
      // const featuredProducts = await getFeaturedProducts();
      const todaysDealProducts = await getTodaysDealProducts();
      const BestSellers = await getBestSellers();
      dispatch(setProducts(products.data.data));
      dispatch(setShopProducts(products.data.data));
      dispatch(setNewProducts(newProducts.data.data));
      dispatch(setTodaysDealProducts(todaysDealProducts.data.data));
      dispatch(setBestSellers(BestSellers.data.data));
    } catch (err) {
      message.error("Failed to load products: " + err.message);
    }
  };

  useEffect(() => {
    if (maintoken) {
      getUser();
      getCartItems();
      getOrderItems();
    }
    getProducts();
  }, [dispatch, maintoken]);

  useEffect(() => {
    const handleErrors = (error) => {
      message.error("Global Error: " + error.message);
    };

    window.addEventListener("error", handleErrors);
    window.addEventListener("unhandledrejection", handleErrors);

    // Clean up
    return () => {
      window.removeEventListener("error", handleErrors);
      window.removeEventListener("unhandledrejection", handleErrors);
    };
  }, []);

  useEffect(() => {
    // Check if the user needs to set a new password
    if (location.state?.requiresPasswordChange) {
      setNewPasswordVisible(true);
    }
  }, [location]);

  return (
    <ErrorBoundary fallback={errorFallback}>
      <GlobalErrorDisplay></GlobalErrorDisplay>
      <Layout>
        <Header id="header">
          <Navbar />
        </Header>
        <Content id="main" style={{ background: colorBgContainer }}>
          <Outlet />
          {/* Modal for setting a new password */}
          <Modal
            title="Set New Password For Your Account"
            visible={newPasswordVisible}
            footer={null}
            centered // Centers the modal
            maskClosable={false} // Prevent closing on background click
            closable={false} // Hides the close button in the top corner
            destroyOnClose={true} // Destroys the modal content on close
            className="password-modal" // Custom class for additional styling
          >
            <Form
              form={newPasswordForm}
              layout="vertical"
              onFinish={handleNewPasswordSubmit}
            >
              <Form.Item
                label="New Password"
                name="newPassword"
                rules={[
                  { required: true, message: "Please enter the new password" },
                  {
                    pattern:
                      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                    message:
                      "Password must be at least 8 characters long, include an uppercase letter, a lowercase letter, a number, and a special character.",
                  },
                ]}
              >
                <Input.Password />
              </Form.Item>
              <Form.Item
                label="Confirm New Password"
                name="confirmNewPassword"
                dependencies={["newPassword"]}
                rules={[
                  {
                    required: true,
                    message: "Please confirm your new password",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("newPassword") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Passwords do not match")
                      );
                    },
                  }),
                ]}
              >
                <Input.Password />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Submit
                </Button>
              </Form.Item>
            </Form>
          </Modal>
        </Content>
        <Footer id="footer">
          <Footy />
        </Footer>
      </Layout>
    </ErrorBoundary>
  );
};

export default Home;
